import { MediaItem } from '../../../api/structs/media';
import { FrameRefPayload } from '../../../api/media';

/**
 * Reference-frame binding for an `ai_video` media item.
 *
 * Two places on the backend describe which still image opens (and optionally
 * closes) a generated video, and they are read in this order:
 *
 *  1. `output_spec.ai_video.reference_frames` — the structured form
 *     (Go `models.FrameRef`). `buildFrameImageRequests`
 *     (pkg/videoGeneration/requestBuilder.go:379) turns it straight into the
 *     provider's frameImages, and `firstReferenceFrame` (:422) reads its first
 *     entry for models that only take a plain reference image.
 *  2. `metadata.reference_image_url` / `metadata.last_reference_image_url` —
 *     what the pipeline stamps at assembly time. The runner falls back to these
 *     when no structured frames are present.
 *
 * `reference_frames` is THREE-valued on the wire, and the empty list is not the
 * absent one:
 *
 *  - absent / `null` — nothing was ever declared; the metadata fallback applies.
 *  - `[]`            — the user cleared every frame. Authoritative: the backend
 *                      persists it and ships NO frames for that generation.
 *  - `[…]`           — the declared list.
 *
 * Collapsing `[]` into "absent" is what made a cleared clip keep DISPLAYING the
 * stale chained still it no longer generates from.
 *
 * The chained "last" frame is the awkward one: `extractLastReferenceImageURL`
 * (requestBuilder.go:442) appends it from METADATA unconditionally, regardless
 * of `reference_frames`. So dropping the chain means clearing that metadata key
 * as well as omitting the frame from the payload — see `metadataSyncForFrames`.
 */
export type FrameSlot = 'first' | 'last';

export interface FrameBinding {
  /** Provider-facing URL. Same ladder the Go side uses: source_url → file_url. */
  url: string;
  /** Media item this frame is bound to, when it is one of ours. */
  mediaId?: string;
}

export interface ReferenceFrameState {
  first: FrameBinding | null;
  last: FrameBinding | null;
  /** User-facing chain switch. False keeps `last` around for a cheap re-enable. */
  lastEnabled: boolean;
}

const trimmed = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/** source_url → file_url, mirroring Go `extractReferenceImageURL`. */
export const bindingFromMediaItem = (item: MediaItem): FrameBinding | null => {
  const url = trimmed(item.metadata?.source_url) || trimmed(item.file_url);
  if (!url) return null;
  return { url, mediaId: item.id };
};

/**
 * True when metadata marks a scene still as a user upload rather than a
 * pipeline-generated image — e.g. `metadata.source === 'upload'`. Uploads
 * (and unrelated stills a user attaches) ride along in `scene.allItems`
 * next to the images the generation actually drew on, so the swap strip
 * needs to tell them apart.
 */
export const isUploadSourcedMediaItem = (item: MediaItem): boolean => {
  const metadata = item.metadata ?? {};
  // Explicit generation provenance always wins, even if `source` is stale/ambiguous.
  if (metadata.source_checkpoint_id || metadata.source_checkpoint_name) return false;
  const source = trimmed(metadata.source).toLowerCase();
  return source.includes('upload');
};

/**
 * Scene stills worth offering as swap candidates in the regenerate modal:
 * generated images only, minus the video item itself. Keeping this here
 * (rather than duplicated at each mount site) is what keeps the two
 * `sceneImages` call sites — MediaEditorSection.tsx and
 * MediaEditorSceneDetailPanel.tsx — in step.
 */
export const filterReferenceFrameCandidates = (
  allItems: MediaItem[],
  excludeId: string
): MediaItem[] =>
  allItems.filter(
    (entry) =>
      entry.type === 'image' && entry.id !== excludeId && !isUploadSourcedMediaItem(entry)
  );

const noFrames = (): ReferenceFrameState => ({ first: null, last: null, lastEnabled: false });

const readStructuredFrames = (item: MediaItem): ReferenceFrameState | null => {
  const aiVideo = (item.output_spec as Record<string, any> | undefined)?.ai_video;
  const frames = aiVideo?.reference_frames;
  if (!Array.isArray(frames)) return null;
  // Present-but-empty means "cleared", not "unspecified" — answer with an
  // unbound state so the UI stops painting whatever metadata still names.
  if (frames.length === 0) return noFrames();

  let first: FrameBinding | null = null;
  let last: FrameBinding | null = null;
  frames.forEach((frame: any) => {
    const url = trimmed(frame?.ref?.url);
    if (!url) return;
    const binding: FrameBinding = { url, mediaId: trimmed(frame?.ref?.media_id) || undefined };
    if (trimmed(frame?.frame) === 'last') {
      last = last ?? binding;
    } else {
      first = first ?? binding;
    }
  });

  if (!first && !last) return null;
  return { first, last, lastEnabled: last !== null };
};

/**
 * The metadata fallback.
 *
 * `last_reference_media_id` is the CLOSING frame's companion id, stamped by
 * `applyReferenceURLsToRows` / `InjectChainedLastFrameURLsFromConnections`
 * (internal/pipeline/assembly.go) in lockstep with `last_reference_image_url`.
 * It arrived late: for years the closing frame was URL-only by construction,
 * which is invisible on a reel that also emits stills (the frame is then one of
 * the clip's own images and resolves by URL) and fatal on a VIDEO-ONLY reel,
 * where nothing on the clip can be matched against and the frame therefore had
 * no media item to regenerate. Clips produced BEFORE that change still carry
 * only the URL — `mediaId` is legitimately undefined there and callers must
 * degrade to a view/swap-only presentation rather than assume a missing id
 * means a missing frame.
 */
const readMetadataFrames = (item: MediaItem): ReferenceFrameState => {
  const metadata = item.metadata ?? {};
  const firstUrl = trimmed(metadata.reference_image_url);
  const lastUrl = trimmed(metadata.last_reference_image_url);
  return {
    first: firstUrl
      ? { url: firstUrl, mediaId: trimmed(metadata.reference_media_id) || undefined }
      : null,
    last: lastUrl
      ? { url: lastUrl, mediaId: trimmed(metadata.last_reference_media_id) || undefined }
      : null,
    lastEnabled: lastUrl !== '',
  };
};

/** What the video is bound to right now, structured form winning over metadata. */
export const readReferenceFrames = (item: MediaItem): ReferenceFrameState =>
  readStructuredFrames(item) ?? readMetadataFrames(item);

/**
 * The `reference_frames` array for the regenerate payload. An unbound or
 * switched-off slot is omitted entirely — never sent as an empty ref, which the
 * provider would reject.
 */
export const buildReferenceFramesPayload = (
  state: ReferenceFrameState,
  supportsLastFrame: boolean
): FrameRefPayload[] => {
  const frames: FrameRefPayload[] = [];
  if (state.first?.url) {
    frames.push({
      ref: { url: state.first.url, ...(state.first.mediaId ? { media_id: state.first.mediaId } : {}) },
      frame: 'first',
    });
  }
  if (supportsLastFrame && state.lastEnabled && state.last?.url) {
    frames.push({
      ref: { url: state.last.url, ...(state.last.mediaId ? { media_id: state.last.mediaId } : {}) },
      frame: 'last',
    });
  }
  return frames;
};

/**
 * Metadata keys to write alongside the payload, keeping the stamped values in
 * step with the structured frames. Clearing `last_reference_image_url` is what
 * actually breaks a chain — the runner appends that URL whether or not
 * `reference_frames` mentions it.
 *
 * The companion IDs are written in lockstep with their URLs, matching the rule
 * `applyReferenceURLsToRows` (internal/pipeline/assembly.go) enforces on the
 * way in — including being blanked when the new binding has no id. An id that
 * disagrees with its URL names a different still, and the id is the half that
 * wins: `extractReferenceImageURL` (pkg/videoGeneration/requestBuilder.go:326)
 * resolves `reference_media_id` BEFORE looking at `reference_image_url`, so a
 * frame swapped from the library used to keep shipping the image it replaced
 * on any later generation that fell back to metadata. The closing id matters
 * for a second reason: the clips UI resolves it to decide whether it may offer
 * to REGENERATE that frame, and a stale id points that button at the wrong
 * image.
 */
export const metadataSyncForFrames = (
  state: ReferenceFrameState,
  supportsLastFrame: boolean
): Record<string, string> => {
  const lastBound = supportsLastFrame && state.lastEnabled && state.last?.url ? state.last : null;
  return {
    reference_image_url: state.first?.url ?? '',
    reference_media_id: state.first?.mediaId ?? '',
    last_reference_image_url: lastBound?.url ?? '',
    last_reference_media_id: lastBound?.mediaId ?? '',
  };
};

/**
 * The scene item behind a frame binding, when it is one of ours — matched by
 * media id first, falling back to a matching provider URL (mirrors the
 * candidate resolution `ReferenceFramesEditor` does for its swap strip).
 * Callers use this to prefer a resolved item's thumbnail derivative and fall
 * back to rendering the binding's raw URL when nothing matches.
 */
export const resolveFrameBindingItem = (
  binding: FrameBinding | null,
  allItems: MediaItem[]
): MediaItem | undefined => {
  if (!binding) return undefined;
  if (binding.mediaId) {
    const byId = allItems.find((entry) => entry.id === binding.mediaId);
    if (byId) return byId;
  }
  // The URL fallback is a heuristic, and the resolved item's THUMBNAIL is what
  // callers paint (the binding URL itself is only their error fallback) — so a
  // wrong match displays the wrong image outright. Uploads ride along in
  // allItems but are never what a clip generation drew on; keep them out of
  // heuristic matching. An explicit media_id match above stays authoritative.
  return allItems.find(
    (entry) =>
      !isUploadSourcedMediaItem(entry) && bindingFromMediaItem(entry)?.url === binding.url
  );
};

const sameBinding = (a: FrameBinding | null, b: FrameBinding | null): boolean =>
  (a?.url ?? '') === (b?.url ?? '') && (a?.mediaId ?? '') === (b?.mediaId ?? '');

/**
 * Did THIS slot move?
 *
 * Per-slot rather than whole-state, because the two slots are written
 * separately: a caller that syncs `last_reference_*` metadata off a change to
 * the FIRST slot alone would blank a closing binding nobody touched.
 */
export const isFrameSlotDirty = (
  initial: ReferenceFrameState,
  current: ReferenceFrameState,
  slot: FrameSlot
): boolean =>
  slot === 'first'
    ? !sameBinding(initial.first, current.first)
    : !sameBinding(initial.last, current.last) || initial.lastEnabled !== current.lastEnabled;

/** True once the user has actually moved something. Untouched → send nothing. */
export const isReferenceFrameStateDirty = (
  initial: ReferenceFrameState,
  current: ReferenceFrameState
): boolean =>
  isFrameSlotDirty(initial, current, 'first') || isFrameSlotDirty(initial, current, 'last');
